import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Avatar, Box, Button, Skeleton, Typography } from "@mui/material";

import { Group } from "../../../../../Groups/api/types/group.types";
import { formatImgUrl } from "../../../../../../utils/cardFormating";
import { CreateNewGroupButtonIcon, GroupCardSeeDetailsButtonIcon } from "../../../../assets/icons/entity-icons";
import { getEntityGroupCreateRoute, getEntityGroupDetailsRoute } from "../../../../../../routers/routeUtils";

export const GroupCard = ({ group }: { group: Group }) => {
  const { t } = useTranslation("profiles", { keyPrefix: "groups" });
  const navigate = useNavigate();
  const { entityId } = useParams<{ entityId: string }>();

  return (
    <Box
      sx={{
        borderRadius: "16px",
        background: "#FFF",
        boxShadow: "0px 12px 20px 0px rgba(0, 0, 0, 0.04), 0px 0px 2px 0px rgba(0, 0, 0, 0.06)",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        padding: "16px",
        "&:hover": {
          border: "1px solid var(--Green---50, #00E606)",
          background: "var(--Green---25, #E7EFED)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "24px",
        }}
      >
        <Avatar
          src={formatImgUrl(group.imageLink)}
          alt={group.name}
          style={{
            width: "72px",
            height: "72px",
          }}
        >
          {group.name.slice(0, 2)}
        </Avatar>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Typography
            sx={{
              color: "var(--Dark---1000, #111)",
              textAlign: "center",
              textOverflow: "ellipsis",
              fontFamily: "Montserrat",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "24px",
            }}
          >
            {group.name}
          </Typography>

          <Typography
            sx={{
              color: "var(--Dark---600, #707070)",
              textAlign: "center",
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "normal",
            }}
          >
            {t("membersNumber", { count: group.users.length })}
          </Typography>
        </Box>
      </Box>
      <Button
        onClick={() => navigate(getEntityGroupDetailsRoute(Number(entityId), group.id))}
        variant="text-sm"
        endIcon={<GroupCardSeeDetailsButtonIcon />}
      >
        {t("seeDetails")}
      </Button>
    </Box>
  );
};

export const CreateNewGroupCard = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "groups" });
  const navigate = useNavigate();
  const { entityId } = useParams<{ entityId: string }>();

  return (
    <Box
      sx={{
        borderRadius: "16px",
        background: "#FFF",
        boxShadow: "0px 12px 20px 0px rgba(0, 0, 0, 0.04), 0px 0px 2px 0px rgba(0, 0, 0, 0.06)",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        padding: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "24px",
        }}
      >
        <Skeleton variant="circular" width={72} height={72} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <Skeleton variant="rounded" width={120} height={15} />
          <Skeleton variant="rounded" width={80} height={10} />
        </Box>
      </Box>
      <Button
        variant="text-sm"
        startIcon={<CreateNewGroupButtonIcon />}
        sx={{
          marginTop: "auto",
        }}
        onClick={() => navigate(getEntityGroupCreateRoute(Number(entityId)))}
      >
        {t("createNew")}
      </Button>
    </Box>
  );
};

export const GroupCardSkeleton = () => {
  return (
    <Box
      sx={{
        borderRadius: "16px",
        background: "#FFF",
        boxShadow: "0px 12px 20px 0px rgba(0, 0, 0, 0.04), 0px 0px 2px 0px rgba(0, 0, 0, 0.06)",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        padding: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "24px",
        }}
      >
        <Skeleton variant="circular" width={72} height={72} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <Skeleton variant="rounded" width={120} height={15} />
          <Skeleton variant="rounded" width={80} height={10} />
        </Box>
      </Box>
      <Skeleton
        variant="rounded"
        width={120}
        height={30}
        sx={{
          margin: "auto auto 0",
        }}
      />
    </Box>
  );
};
